import { useQueries, useQuery } from '@tanstack/react-query';
import { useSharedRecordSearch } from '../../components/SharedRecord/useSharedRecordSearch';
import { useAuth } from '../../contexts/authContext';
import { registryHref } from '../../pages/Registries/registryHref';
import { artifactClient, authClient, contractModelClient, deliveryClient, projectDataClient } from '../connect/clients';
const KIND_LABEL = {
    program: 'Program',
    deliverable: 'Deliverable',
    document: 'Document',
    person: 'Person',
    pursuit: 'Pursuit',
    company: 'Company',
    vehicle: 'Contract vehicle',
    library: 'Library item'
};
export const MENTION_SCHEME = 'mention:';
export function mentionHref(kind, id) {
    return `${MENTION_SCHEME}${kind}/${encodeURIComponent(id)}`;
}
export function parseMentionHref(href) {
    if (!href?.startsWith(MENTION_SCHEME)) return null;
    const [kind, ...rest] = href.slice(MENTION_SCHEME.length).split('/');
    let id;
    try {
        id = decodeURIComponent(rest.join('/'));
    } catch  {
        // Malformed percent-encoding: an ordinary bad link, not a crash.
        return null;
    }
    if (!id || !Object.keys(KIND_LABEL).includes(kind)) return null;
    return {
        kind: kind,
        id
    };
}
/** A deliverable mention's id: its program and its deliverable, "programId/dutyId". */ export function deliverableMentionId(programId, dutyId) {
    return `${programId}/${dutyId}`;
}
/**
 * The app page a mention chip opens, or null when it has none: a document
 * opens in place, and a person has no page everyone can open.
 */ export function mentionPath(kind, id) {
    const e = encodeURIComponent(id);
    switch(kind){
        case 'program':
            return `/delivery/programs/${e}`;
        case 'deliverable':
            {
                const [programId, dutyId] = id.split('/');
                if (!programId || !dutyId) return null;
                return `/delivery/programs/${encodeURIComponent(programId)}/deliverables/${encodeURIComponent(dutyId)}`;
            }
        case 'pursuit':
            return `/capture/${e}`;
        case 'company':
            return registryHref({
                kind: 'COMPANY',
                id
            });
        case 'vehicle':
            return registryHref({
                kind: 'VEHICLE',
                id
            });
        case 'library':
            return `/library?record=${e}`;
        case 'document':
        case 'person':
            return null;
    }
}
/** Text with each mention link shown as its label ("@OMEGA"), for plain-text places like titles. */ export function mentionsAsText(text) {
    return text.replace(/\[(@[^\]]*)\]\(mention:[^)\s]*\)/g, '$1');
}
export function mentionKindLabel(kind) {
    return KIND_LABEL[kind];
}
/** The text typed after an @ right before the caret, or null when not mentioning. */ export function mentionQuery(textBeforeCaret) {
    const m = /(?:^|\s)@([^\s@]{0,40})$/.exec(textBeforeCaret);
    return m ? m[1] : null;
}
const MAX = 10;
const PER_KIND = 3;
/** Deliverables are listed per program; search this many programs at most. */ const DELIVERABLE_PROGRAMS = 12;
/** Deliverables are searched only once the query is this long, to avoid a fan-out on a bare @. */ const DELIVERABLE_MIN_QUERY = 2;
const has = (s, q)=>!!s && s.toLowerCase().includes(q.toLowerCase());
/**
 * Whether a deliverable matches the text after @: its title, PWS reference,
 * aliases or key. A CLIN family key ("x001AD") also matches a CLIN typed as
 * the app shows it ("0001AD"): any leading digit, then the family's rest.
 */ export function deliverableMatches(d, q) {
    if (!q) return true;
    if ([
        d.title,
        d.pwsRef,
        d.dutyKey,
        ...d.aliases
    ].some((t)=>has(t, q))) return true;
    const family = /^x(\d+[A-Za-z]*)$/i.exec(d.dutyKey.trim());
    const typed = /^(\d)(\w*)$/.exec(q.trim());
    return !!family && !!typed && family[1].toLowerCase().startsWith(typed[2].toLowerCase());
}
/**
 * Up to PER_KIND hits of each kind, in group order, then any room left filled
 * from the rest in the same order, so one busy kind can't hide the others.
 */ export function pickMentionHits(groups, max = MAX, perKind = PER_KIND) {
    const first = groups.flatMap((g)=>g.slice(0, perKind));
    const rest = groups.flatMap((g)=>g.slice(perKind));
    return [
        ...first,
        ...rest
    ].slice(0, max);
}
/**
 * The record lists the @ menu and the new-chat starters share. One query key
 * per list, so both read the same cache.
 */ export function programsQuery(tenantId, search, enabled) {
    return {
        queryKey: [
            'mention-programs',
            tenantId,
            search
        ],
        enabled: enabled && !!tenantId,
        staleTime: 30000,
        queryFn: async ()=>(await deliveryClient.listPrograms({
                companyId: tenantId,
                search,
                sectorId: '',
                businessUnitId: '',
                portfolioId: '',
                operationalStateId: ''
            })).programs
    };
}
export function pursuitsQuery(tenantId, enabled) {
    return {
        queryKey: [
            'mention-pursuits',
            tenantId
        ],
        enabled: enabled && !!tenantId,
        staleTime: 60000,
        retry: false,
        queryFn: async ()=>(await projectDataClient.listCaptureProjects({})).captures
    };
}
export function documentsQuery(tenantId, userId, enabled) {
    return {
        queryKey: [
            'mention-documents',
            tenantId,
            userId
        ],
        enabled: enabled && !!userId,
        staleTime: 60000,
        retry: false,
        queryFn: async ()=>(await artifactClient.listArtifacts({
                filter: {
                    ownerUserId: userId
                },
                offset: 0,
                limit: 100
            })).artifacts
    };
}
/**
 * Records matching the text after @: programs, deliverables, documents and
 * people first, then pursuits and shared records.
 */ export function useMentionSearch(query) {
    const { user } = useAuth();
    const tenantId = user?.tenant_id ?? '';
    const userId = user?.id ?? '';
    const active = query !== null;
    const q = (query ?? '').trim();
    const programs = useQuery(programsQuery(tenantId, q, active));
    const searchDeliverables = active && !!tenantId && q.length >= DELIVERABLE_MIN_QUERY;
    const allPrograms = useQuery(programsQuery(tenantId, '', searchDeliverables));
    const dutyPrograms = (allPrograms.data ?? []).slice(0, DELIVERABLE_PROGRAMS);
    const duties = useQueries({
        queries: dutyPrograms.map((p)=>({
                queryKey: [
                    'mention-duties',
                    p.id
                ],
                enabled: searchDeliverables,
                staleTime: 60000,
                retry: false,
                queryFn: async ()=>{
                    const resp = await contractModelClient.listDuties({
                        body: {
                            programId: p.id
                        }
                    });
                    return resp.body?.duties ?? [];
                }
            }))
    });
    const documents = useQuery(documentsQuery(tenantId, userId, active));
    const people = useQuery({
        queryKey: [
            'mention-people',
            tenantId,
            q
        ],
        enabled: active && !!tenantId,
        staleTime: 60000,
        retry: false,
        queryFn: async ()=>(await authClient.listUsersForSharing({
                query: q
            })).users
    });
    const pursuits = useQuery(pursuitsQuery(tenantId, active));
    const shared = useSharedRecordSearch(active ? q : '');
    const dutyData = duties.map((d)=>d.data);
    const hits = (()=>{
        if (!active) return [];
        const programHits = [];
        for (const p of programs.data ?? []){
            const label = p.programNameShort || p.programNameFull;
            if (!label) continue;
            programHits.push({
                kind: 'program',
                id: p.id,
                label,
                context: KIND_LABEL.program
            });
        }
        const deliverableHits = [];
        dutyPrograms.forEach((p, i)=>{
            const programName = p.programNameShort || p.programNameFull || 'Program';
            for (const d of dutyData[i] ?? []){
                if (d.endedOn) continue;
                if (!deliverableMatches(d, q)) continue;
                deliverableHits.push({
                    kind: 'deliverable',
                    id: deliverableMentionId(p.id, d.id),
                    label: d.title || 'Untitled deliverable',
                    context: [
                        KIND_LABEL.deliverable,
                        d.pwsRef ? `PWS ${d.pwsRef}` : '',
                        programName
                    ].filter(Boolean).join(' · ')
                });
            }
        });
        const documentHits = [];
        for (const a of documents.data ?? []){
            const label = a.displayTitle || a.filename;
            if (!label || q && !has(a.filename, q) && !has(a.displayTitle, q)) continue;
            documentHits.push({
                kind: 'document',
                id: a.artifactId,
                label,
                context: KIND_LABEL.document
            });
        }
        const personHits = [];
        for (const u of people.data ?? []){
            if (!u.id || u.id === userId) continue;
            personHits.push({
                kind: 'person',
                id: u.id,
                label: u.name || u.email,
                context: u.email ? `${KIND_LABEL.person} · ${u.email}` : KIND_LABEL.person
            });
        }
        const pursuitHits = [];
        for (const c of pursuits.data ?? []){
            if (q && !has(c.name, q)) continue;
            pursuitHits.push({
                kind: 'pursuit',
                id: c.captureProjectId,
                label: c.name || 'Pursuit',
                context: KIND_LABEL.pursuit
            });
        }
        const sharedHits = [];
        for (const h of shared.data?.hits ?? []){
            // Programs and pursuits have their own sources above; the rest are not mentionable.
            if (h.kind === 'labor' || h.kind === 'program' || h.kind === 'pursuit' || h.kind === 'landscape') continue;
            sharedHits.push({
                kind: h.kind,
                id: h.id,
                label: h.title,
                context: h.context
            });
        }
        return pickMentionHits([
            programHits,
            deliverableHits,
            documentHits,
            personHits,
            pursuitHits,
            sharedHits
        ]);
    })();
    return {
        hits,
        loading: programs.isLoading || pursuits.isLoading
    };
}
